import { useState } from 'react'
import { useDispatch } from 'react-redux'

import parentStyles from '../styles.module.scss'
import IpAllowlistEditModal from './IpAllowlistEditModal'

import Button from '@/components/Button'
import ConfirmDialog from '@/components/ConfirmDialog'
import { Switch } from '@/components/Switch'
import { useToast } from '@/components/Toast/ToastContext'
import { AppDispatch } from '@/store'
import { updateEnterpriseSettingsThunk } from '@/store/features/user/userSlice'
import { EnterpriseSettings, SettingType } from '@/types'

interface IpAllowlistCardProps {
  settings: EnterpriseSettings
  isAdmin: boolean
}

const IpAllowlistCard = ({ settings, isAdmin }: IpAllowlistCardProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const { updateToast } = useToast()
  const [modalOpen, setModalOpen] = useState(false)
  const [showDisableConfirm, setShowDisableConfirm] = useState(false)
  const [isDisabling, setIsDisabling] = useState(false)

  const allowlist = settings[SettingType.ipAllowlist] ?? []
  const isEnabled = allowlist.length > 0
  const isLocked = settings[SettingType.ipAllowlistLocked] === true

  const label = isLocked
    ? 'IP allowlist (locked by Wispr)'
    : isEnabled
      ? `IP allowlist (${allowlist.length} CIDR${allowlist.length === 1 ? '' : 's'})`
      : 'IP allowlist'

  const handleToggle = (next: boolean) => {
    if (next) {
      // Toggle ON: open the modal so the user can enter CIDRs. The Switch is
      // controlled by the saved state (isEnabled), so the toggle stays
      // visually OFF until the modal save persists a non-empty list.
      setModalOpen(true)
    } else {
      // Toggle OFF: confirm before clearing the saved list. Toggle stays
      // visually ON until confirm dispatches a save.
      setShowDisableConfirm(true)
    }
  }

  const handleDisable = async () => {
    setIsDisabling(true)
    try {
      await dispatch(
        updateEnterpriseSettingsThunk({ settings: { [SettingType.ipAllowlist]: null } })
      ).unwrap()
      updateToast('IP allowlist disabled', 'success')
      setShowDisableConfirm(false)
    } catch (error) {
      updateToast(typeof error === 'string' ? error : 'Failed to disable IP allowlist', 'error')
    } finally {
      setIsDisabling(false)
    }
  }

  return (
    <>
      <div className={parentStyles.settingsItem}>
        <div className={parentStyles.toggleLabel}>{label}</div>
        <Switch checked={isEnabled} onInputChange={handleToggle} disabled={!isAdmin || isLocked} />
        {isEnabled && !isLocked && (
          <Button
            title="Configure"
            onClick={() => setModalOpen(true)}
            type="dark"
            className={parentStyles.controlButton}
            isDisabled={!isAdmin}
          />
        )}
      </div>
      <IpAllowlistEditModal isOpen={modalOpen} close={() => setModalOpen(false)} currentSettings={settings} />
      <ConfirmDialog
        isOpen={showDisableConfirm}
        title="Disable IP allowlist?"
        message="Your saved IP ranges will be cleared. You'll need to re-enter them to re-enable."
        confirmLabel="Disable"
        cancelLabel="Keep enabled"
        confirmType="destructive"
        isConfirming={isDisabling}
        onConfirm={() => void handleDisable()}
        onCancel={() => setShowDisableConfirm(false)}
      />
    </>
  )
}

export default IpAllowlistCard
